import React, { useState, useEffect, useCallback, useRef } from 'react';
import LoadingScreen from './components/LoadingScreen';
import MapAndControls from './components/MapAndControls';
import WeatherDisplay from './components/WeatherDisplay';
import Toast from './components/Toast';
import { AppNavigation } from './components/AppNavigation';

import { HourlyWeather, DailyWeather, FishingScore } from './models/WeatherData';
import { WeatherAPI } from './services/api';
import { useSettings } from './contexts/SettingsContext';
import { useLanguage } from './contexts/LanguageContext';
import './App.css';

function App() {
  const { defaultSpecies } = useSettings();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  // Note: showWelcome and showTutorial are now handled by parent App.tsx
  const [coordinates, setCoordinates] = useState({ lat: 59.3293, lon: 18.0686 }); // Stockholm default
  const [coordinatesInitialized, setCoordinatesInitialized] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [locationName, setLocationName] = useState('Stockholm');
  const [currentWeather, setCurrentWeather] = useState<HourlyWeather | null>(null);
  const [hourlyWeather, setHourlyWeather] = useState<HourlyWeather[]>([]);
  const [dailyWeather, setDailyWeather] = useState<DailyWeather[]>([]);
  const [fishingScores, setFishingScores] = useState<FishingScore[]>([]);
  const [dailyFishingScores, setDailyFishingScores] = useState<FishingScore[]>([]);
  const [toast, setToast] = useState<{ message: string; id: number } | null>(null);
  const [activeSection, setActiveSection] = useState<string>('map');

  // Refs for sections
  const mapRef = useRef<HTMLDivElement>(null);
  const weatherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Note: Welcome screen is now handled by parent App.tsx
    // No need to check weatherAppOnboarding here
    
    // Check URL parameters
    const params = new URLSearchParams(window.location.search);
    const urlLat = params.get('lat');
    const urlLon = params.get('lon');
    const urlDate = params.get('date');
    
    if (urlLat && urlLon) {
      const coords = { lat: parseFloat(urlLat), lon: parseFloat(urlLon) };
      setCoordinates(coords);
      // Save to localStorage for future visits
      localStorage.setItem('lastCoordinates', JSON.stringify(coords));
      setCoordinatesInitialized(true);
    } else {
      // Try to get saved coordinates from localStorage
      const savedCoords = localStorage.getItem('lastCoordinates');
      if (savedCoords) {
        const coords = JSON.parse(savedCoords);
        setCoordinates(coords);
        // Update URL with saved coordinates
        const url = new URL(window.location.href);
        url.searchParams.set('lat', coords.lat.toString());
        url.searchParams.set('lon', coords.lon.toString());
        window.history.replaceState({}, '', url.toString());
        setCoordinatesInitialized(true);
      } else {
        // Try to get current location as last resort
        getCurrentLocation();
        // Note: coordinatesInitialized will be set in getCurrentLocation callback
      }
    }
    
    if (urlDate) {
      setSelectedDate(urlDate);
    } else {
      // Set initial URL params if not present
      const url = new URL(window.location.href);
      if (!url.searchParams.has('date')) {
        url.searchParams.set('date', selectedDate);
        window.history.replaceState({}, '', url.toString());
      }
    }

    // Simulate loading
    setTimeout(() => setIsLoading(false), 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch weather data whenever coordinates, date, or species changes
  useEffect(() => {
    if (!coordinates || !coordinatesInitialized) return;

    console.log('🔄 FETCHING WEATHER DATA:', {
      lat: coordinates.lat,
      lng: coordinates.lon,
      date: selectedDate,
      species: defaultSpecies
    });

    const fetchData = async () => {
      try {
        console.log('📡 API CALL: getComplete()');
        
        // Use the complete API endpoint to get everything in one call
        const response = await WeatherAPI.getComplete(
          coordinates.lat,
          coordinates.lon,
          defaultSpecies,
          'medium', // TODO: Get from settings
          selectedDate, // Pass selected date!
          language // Use language from context
        );

        console.log('✅ API RESPONSE:', response);

        // Extract location name
        setLocationName(response.location.name);

        // Extract weather data - note: CompleteWeatherResponse has these at top level
        const { current, hourly, daily } = response;

        console.log('📊 Hourly data sample (first 3 hours):', hourly.slice(0, 3).map(h => ({
          hour: h.hour,
          temp: h.temperature,
          fishingScore: h.fishingScore ? {
            score: h.fishingScore.score,
            factorsCount: h.fishingScore.factors?.length || 0,
            criticalCount: h.fishingScore.criticalFactors?.length || 0
          } : 'NO SCORE'
        })));

        // Set daily weather (now includes fishingScore on each day)
        setDailyWeather(daily);

        // Set hourly weather (now includes fishingScore on each hour)
        setHourlyWeather(hourly);
        
        // Set current weather
        setCurrentWeather(current);

        // Extract fishing scores from the hourly data
        const hourlyScores: FishingScore[] = hourly.map(hour => {
          if (hour.fishingScore) {
            return hour.fishingScore as FishingScore;
          }
          // Fallback if no score
          return {
            score: 50,
            factors: [],
            criticalFactors: [],
            debug: {
              sLight: '', sPress: '', sWind: '', sPC: '', sMoon: '', sSea: '', sStab: '',
              frontMul: '', waterTemp: '', trend6h: '', trend24h: '', hourIndex: 0, cloudiness: ''
            }
          };
        });
        setFishingScores(hourlyScores);

        // Extract daily fishing scores from the daily data
        const dailyScores: FishingScore[] = daily.map(day => {
          if (day.fishingScore) {
            return day.fishingScore as FishingScore;
          }
          // Fallback if no score
          return {
            score: 50,
            factors: [],
            criticalFactors: [],
            debug: {
              sLight: '', sPress: '', sWind: '', sPC: '', sMoon: '', sSea: '', sStab: '',
              frontMul: '', waterTemp: '', trend6h: '', trend24h: '', hourIndex: 0, cloudiness: ''
            }
          };
        });
        setDailyFishingScores(dailyScores);

      } catch (error) {
        console.error('Failed to fetch weather data:', error);
        showToastMessage('⚠️ Kunde inte hämta väderdata');
      }
    };

    fetchData();
  }, [coordinates, selectedDate, defaultSpecies, coordinatesInitialized, language]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setCoordinatesInitialized(true);
      showToastMessage('⚠️ Geolocation stöds inte av din webbläsare');
      return;
    }

    setIsLoading(true);
    showToastMessage('📍 Letar efter din position...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };
        setCoordinates(coords);
        setCoordinatesInitialized(true);
        
        // Save to localStorage
        localStorage.setItem('lastCoordinates', JSON.stringify(coords));
        
        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('lat', coords.lat.toString());
        url.searchParams.set('lon', coords.lon.toString());
        window.history.pushState({}, '', url.toString());
        
        setIsLoading(false);
        showToastMessage('✅ Din plats har hittats!');
      },
      (error) => {
        console.error('Geolocation error:', error);
        setCoordinatesInitialized(true);
        setIsLoading(false);
        
        // More specific error messages
        switch (error.code) {
          case error.PERMISSION_DENIED:
            showToastMessage('⚠️ Du nekade åtkomst till din position. Tillåt platsåtkomst i webbläsarens inställningar.');
            break;
          case error.POSITION_UNAVAILABLE:
            showToastMessage('⚠️ Din position kunde inte bestämmas.');
            break;
          case error.TIMEOUT:
            showToastMessage('⚠️ Tidsgränsen uppnåddes för att hitta din position.');
            break;
          default:
            showToastMessage('⚠️ Kunde inte hitta din plats - använder Stockholm som standard');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleLocationChange = useCallback((coords: { lat: number; lon: number }) => {
    setCoordinates(coords);
    setCoordinatesInitialized(true);
    
    // Save to localStorage
    localStorage.setItem('lastCoordinates', JSON.stringify(coords));
    
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('lat', coords.lat.toString());
    url.searchParams.set('lon', coords.lon.toString());
    window.history.pushState({}, '', url.toString());
    
    showToastMessage(`📍 Plats vald: ${coords.lat.toFixed(2)}, ${coords.lon.toFixed(2)}`);
  }, []);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('date', date);
    window.history.pushState({}, '', url.toString());
  };

  const handleNavigation = (section: 'map' | 'weather' | 'ai' | 'favorites' | 'settings' | 'history' | 'hourly' | 'fishing' | 'wind') => {
    setActiveSection(section);
    
    // Scroll to section with smooth behavior
    const offset = 80; // Account for sticky nav height
    
    if (section === 'map' && mapRef.current) {
      const top = mapRef.current.offsetTop - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    } else if (section === 'weather' && weatherRef.current) {
      const top = weatherRef.current.offsetTop - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    } else if (section === 'hourly') {
      // Find hourly section within WeatherDisplay
      const hourlySection = document.querySelector('[data-section="hourly"]');
      if (hourlySection) {
        const top = (hourlySection as HTMLElement).offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else if (section === 'history') {
      const historySection = document.querySelector('[data-section="history"]');
      if (historySection) {
        const top = (historySection as HTMLElement).offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else if (section === 'fishing') {
      const fishingSection = document.querySelector('[data-section="fishing"]');
      if (fishingSection) {
        const top = (fishingSection as HTMLElement).offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else if (section === 'wind') {
      const windSection = document.querySelector('[data-section="wind"]');
      if (windSection) {
        const top = (windSection as HTMLElement).offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else if (section === 'ai') {
      // Find AI section within WeatherDisplay
      const aiSection = document.querySelector('[data-section="ai"]');
      if (aiSection) {
        const top = (aiSection as HTMLElement).offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else if (section === 'favorites') {
      // Scroll to favorites section in MapAndControls
      const favSection = document.getElementById('favorites-container-wrapper');
      if (favSection && mapRef.current) {
        // Scroll to map first (where favorites are)
        const top = mapRef.current.offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else if (section === 'settings') {
      // Open settings modal
      const settingsBtn = document.querySelector('button[aria-label*="nställning"]') as HTMLButtonElement;
      if (settingsBtn) {
        settingsBtn.click();
      }
    }
  };

  // Detect active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for better detection
      
      // Get all section positions
      const weatherTop = weatherRef.current?.offsetTop || 0;
      const hourlyEl = document.querySelector('[data-section="hourly"]') as HTMLElement;
      const historyEl = document.querySelector('[data-section="history"]') as HTMLElement;
      const fishingEl = document.querySelector('[data-section="fishing"]') as HTMLElement;
      const windEl = document.querySelector('[data-section="wind"]') as HTMLElement;
      const aiEl = document.querySelector('[data-section="ai"]') as HTMLElement;
      
      const hourlyTop = hourlyEl?.offsetTop || Infinity;
      const historyTop = historyEl?.offsetTop || Infinity;
      const fishingTop = fishingEl?.offsetTop || Infinity;
      const windTop = windEl?.offsetTop || Infinity;
      const aiTop = aiEl?.offsetTop || Infinity;
      
      // Determine which section is active
      if (scrollPosition >= aiTop) {
        setActiveSection('ai');
      } else if (scrollPosition >= windTop) {
        setActiveSection('wind');
      } else if (scrollPosition >= fishingTop) {
        setActiveSection('fishing');
      } else if (scrollPosition >= historyTop) {
        setActiveSection('history');
      } else if (scrollPosition >= hourlyTop) {
        setActiveSection('hourly');
      } else if (scrollPosition >= weatherTop) {
        setActiveSection('weather');
      } else {
        setActiveSection('weather');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const showToastMessage = (message: string) => {
    const id = Date.now();
    setToast({ message, id });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      {/* Note: WelcomeScreen and Tutorial are now handled by parent App.tsx */}
      
      <div className="weather-grid">
            {/* Left columns: Weather Display (2 columns on desktop) */}
            <div ref={weatherRef} className="weather-main-content space-y-4 sm:space-y-6 order-2 lg:order-1">
              {/* App Navigation - only in left column */}
              <AppNavigation onNavigate={handleNavigation} activeSection={activeSection} />
              
              <WeatherDisplay
                locationName={locationName}
                currentWeather={currentWeather}
                hourlyWeather={hourlyWeather}
                dailyWeather={dailyWeather}
                fishingScores={fishingScores}
                dailyFishingScores={dailyFishingScores}
                selectedDate={selectedDate}
                coordinates={coordinates ? { lat: coordinates.lat, lng: coordinates.lon } : null}
                species={defaultSpecies}
              />
            </div>

            {/* Right column: Map and Controls (1 column) */}
            <div ref={mapRef} className="weather-sidebar order-1 lg:order-2">
              <MapAndControls
                coordinates={coordinates}
                selectedDate={selectedDate}
                locationName={locationName}
                onDateChange={handleDateChange}
                onLocationChange={handleLocationChange}
                onCurrentLocation={getCurrentLocation}
                onShowToast={showToastMessage}
              />
            </div>
          </div>

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} />}
    </>
  );
}

export default App;
